import Collection from "@arcgis/core/core/Collection";
import { faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueryCache } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { AutoSizer, Column, Table } from "react-virtualized";
import {
  Button,
  Dimmer,
  Divider,
  Dropdown,
  Header,
  Icon,
  Input,
  Loader,
  Message,
  Modal,
  Segment,
} from "semantic-ui-react";
import { useClient } from "../../../../hooks/useClient";
import { incrementName } from "../../../../utils/incrementName";
import { useFeatureEditorHistory } from "../../hooks/editor/useFeatureEditorHistory";
import { useImportLayer } from "../../hooks/editor/useImportLayer";
import { useLayerFieldDefinitions } from "../../hooks/editor/useLayerFieldDefinitions";
import { useMapFeatures } from "../../hooks/editor/useMapFeatures";
import { SketchTool, useSketchEvents } from "../../hooks/editor/useSketchEvents";
import { useMapInitialization } from "../../hooks/useMapInit";
import {
  getMapLayerQueryKey,
  MAP_LAYERS_LIST_QUERY_KEY,
  useCreateMapLayer,
  useDeactivateMapLayer,
  useReactivateMapLayer,
  useUpdateMapLayer,
} from "../../hooks/useMapLayer";
import { getHoverSymbolForGeometryType, getSymbolForGeometryType } from "../../utils/arcgisHelper";
import { ImportLayerModal } from "./ImportLayerModal";
import { MapEditorToolbar } from "./MapManagementEditToolbar";
import "./MapManagementTable.scss";
import { ToastStatuses } from "../../../../components/global_components/Toast";

export const graphicIndex = new Map<string, number>();

export function MapManagementEdit() {
  const { site, id: layerId } = useParams<{ site: string; id?: string }>();
  const [layerName, setLayerName] = useState("");
  const [layerNameError, setLayerNameError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoveredFeatureId, setHoveredFeatureId] = useState<string | null>(null);
  const { addToast } = useToasts();
  const history = useHistory();
  const queryCache = useQueryCache();
  const client = useClient(site);
  const cityDefaultCenter = client.query.data?.defaultCenter;
  const defaultCenter = useMemo(
    () => (cityDefaultCenter ? [cityDefaultCenter.lng, cityDefaultCenter.lat] : [-73.0193, 40.766]),
    [client.query.data],
  );

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableContainerHeight, setTableContainerHeight] = useState(0);

  const { deactivateLayer } = useDeactivateMapLayer();
  const { reactivateLayer } = useReactivateMapLayer();
  const { createLayer } = useCreateMapLayer();
  const { updateLayer } = useUpdateMapLayer();

  const { mapRef, viewRef, sketchRef, sketchInitialized, editLayerRef, zoomRef } = useMapInitialization({
    center: defaultCenter as [number, number],
  });

  const {
    fieldDefinitions,
    setFieldDefinitions,
    layerQuery,
    handleAddColumn,
    handleDeleteColumn,
    handleUpdateColumnLabel,
    handleSetPrimaryColumn,
    prepareFieldsForSubmission,
    saveFieldDefinitions,
    hasUnsavedChanges,
  } = useLayerFieldDefinitions({ layerId, editLayerRef });

  useEffect(() => layerQuery.data && setLayerName(layerQuery.data.name ?? ""), [layerQuery.data?.name]);

  // Handles dynamic virtualized table height
  useEffect(() => {
    const updateHeight = () => {
      if (tableContainerRef.current) {
        setTableContainerHeight(tableContainerRef.current.clientHeight);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const markFeatureAsChangedRef = useRef<((graphicId: string) => void) | null>(null);
  const markFeatureAsChangedDebouncedRef = useRef<((graphicId: string) => void) | null>(null);
  const {
    selectedFeatures,
    featureTracking,
    selectedTool,
    handleToolSelect,
    setFeatureTracking,
    selectedFeatureId,
    setSelectedFeatureId,
    updateFeatureField,
    deleteFeature,
    maxFeatureIndex,
    showFeaturePopup,
    readdHandlers,
    sketchCanUndo,
    sketchCanRedo,
  } = useSketchEvents({
    sketchRef,
    sketchInitialized,
    editLayerRef,
    fieldDefinitions,
    handleAddColumn,
    markFeatureAsChanged: markFeatureAsChangedRef.current,
    markFeatureAsChangedDebounced: markFeatureAsChangedDebouncedRef.current,
  });

  const { isSaving, saveFeatures, areFeaturesLoading } = useMapFeatures({
    layerId,
    editLayerRef,
    featureTracking,
    setFeatureTracking,
    fieldDefinitions,
    prepareFieldsForSubmission,
    saveFieldDefinitions,
    hasUnsavedFieldChanges: hasUnsavedChanges,
    maxFeatureIndex,
    markFeatureAsChangedDebounced: markFeatureAsChangedDebouncedRef.current,
  });

  const {
    handleUndo,
    handleRedo,
    canUndo,
    canRedo,
    startBatch,
    endBatch,
    markFeatureAsChanged,
    markFeatureAsChangedDebounced,
  } = useFeatureEditorHistory({
    editLayerRef,
    sketchRef,
    fieldDefinitions,
    setFeatureTracking,
    setFieldDefinitions,
    readdHandlers,
    selectedFeatures,
    sketchCanUndo,
    sketchCanRedo,
    sketchTool: selectedTool,
  });
  markFeatureAsChangedRef.current = markFeatureAsChanged;
  markFeatureAsChangedDebouncedRef.current = markFeatureAsChangedDebounced;

  const {
    isModalOpen: isImportModalOpen,
    setIsModalOpen: setIsImportModalOpen,
    isImporting,
    handleFileUpload,
    handleImport,
    fileContent,
    originalKML,
    fileType,
    wipeState,
  } = useImportLayer({
    editLayerRef,
    setFeatureTracking,
    maxFeatureIndex,
    fieldDefinitions,
    handleAddColumn,
    markFeatureAsChangedDebounced: markFeatureAsChangedDebouncedRef.current,
  });

  const handleImportLayer = () => {
    setIsImportModalOpen(true);
  };

  const tableFeatures = useMemo(() => {
    const features = editLayerRef.current?.graphics;

    // Clear and rebuild the index
    graphicIndex.clear();
    features?.forEach((graphic, index) => {
      if (!graphic.attributes?.graphicId) return;
      graphicIndex.set(graphic.attributes.graphicId, index);
    });

    return (
      features?.filter((g) => !featureTracking[g.attributes?.graphicId]?.isDeleted) ?? new Collection<__esri.Graphic>()
    );
  }, [editLayerRef.current?.graphics, featureTracking, selectedFeatureId]);

  const handleSelectFeatureFromTable = async (graphicId: string) => {
    setSelectedFeatureId(graphicId);
    if (!graphicId) return;

    // Also select the feature on the map
    if (!sketchRef.current || !editLayerRef.current) return;
    const graphics = editLayerRef.current.graphics;
    if (!graphicIndex.has(graphicId)) return;
    const index = graphicIndex.get(graphicId);
    let selectedGraphic = graphics.getItemAt(index!);
    if (selectedGraphic?.attributes.graphicId !== graphicId) {
      // So this is like a super weird bug where the graphicIndex is not updated.
      // It only happens when you select a graphic and then selected a different one
      // That's further down the list. And it also fixes it self the next selection.
      // It's probably some super obscure state issue but this should only execute
      // once, so its not a big performance issue.
      console.error("Graphic ID mismatch");
      selectedGraphic = graphics.find((g) => g.attributes.graphicId === graphicId)!;
    }
    sketchRef.current.cancel();
    sketchRef.current.update([selectedGraphic]);
    if (!selectedGraphic.geometry) return;
    viewRef.current?.goTo({ target: selectedGraphic.geometry });
  };

  // Calculate table height
  const tableHeight = useMemo(() => {
    return Math.min(Math.max(tableFeatures.length * 45, 150), tableContainerHeight);
  }, [tableFeatures.length, tableContainerHeight]);

  // Highlight feature when hovering over table row
  const handleFeatureHover = (graphicId: string | null) => {
    if (hoveredFeatureId === graphicId) return;

    // Restore previous hovered feature's original symbol
    if (hoveredFeatureId && graphicIndex.has(hoveredFeatureId) && hoveredFeatureId !== selectedFeatureId) {
      const prevGraphic = editLayerRef.current?.graphics.at(graphicIndex.get(hoveredFeatureId)!);
      // When a user hovers and then **selects** a feature, the index will be out of sync
      // Check that the prevGraphic actually matches the hoveredFeatureID before resetting the symbol
      // It's fine that this is then skipped, since the click handler will handle the symbol change
      if (prevGraphic?.geometry && prevGraphic.attributes.graphicId === hoveredFeatureId) {
        prevGraphic.symbol = getSymbolForGeometryType(prevGraphic.geometry.type);
      }
    }

    // Apply hover style to new hovered feature
    if (graphicId && graphicIndex.has(graphicId)) {
      const graphic = editLayerRef.current?.graphics.at(graphicIndex.get(graphicId)!);
      // When a user hovers and then **selects** a feature, the index will be out of sync
      // Check that the prevGraphic actually matches the hoveredFeatureID before resetting the symbol
      // It's fine that this is then skipped, since the click handler will handle the symbol change
      if (graphic?.geometry && graphic.attributes.graphicId === graphicId) {
        graphic.symbol = getHoverSymbolForGeometryType(graphic.geometry.type);
      }
    }

    setHoveredFeatureId(graphicId);
  };

  const invalidateLayerQueries = (layerId: string) => {
    if (!layerId) return;
    queryCache.invalidateQueries(MAP_LAYERS_LIST_QUERY_KEY);
    queryCache.invalidateQueries(getMapLayerQueryKey(layerId));
  };

  const handleDeactivate = async () => {
    if (!layerId) return;
    await deactivateLayer(+layerId);
    invalidateLayerQueries(layerId);
    addToast(`Layer "${layerName}" deactivated`, { appearance: ToastStatuses.Success, autoDismiss: true });
    history.push(`/${site}/admin/mapping`);
  };

  const handleReactivate = async () => {
    if (!layerId) return;
    await reactivateLayer(+layerId);
    invalidateLayerQueries(layerId);
    addToast(`Layer "${layerName}" reactivated`, { appearance: ToastStatuses.Success, autoDismiss: true });
  };

  const handleAddNewColumn = () => {
    startBatch();
    const defaultName = incrementName("New Column", fieldDefinitions, (fd) => fd.customField.label);
    handleAddColumn(defaultName);
    endBatch();
  };

  const handleSave = async () => {
    if (!layerName.trim()) return setLayerNameError("Layer name is required");
    setLayerNameError(null);

    let newLayerId: string | number | undefined = layerId;
    if (layerId) {
      await updateLayer({ id: +layerId, data: { name: layerName } });
      invalidateLayerQueries(layerId);
      addToast("Layer updated successfully, saving features...", {
        appearance: ToastStatuses.Success,
        autoDismiss: true,
      });
    } else {
      const newLayer = await createLayer({ name: layerName });
      newLayerId = newLayer?.[0].id;
      queryCache.invalidateQueries(MAP_LAYERS_LIST_QUERY_KEY);
      addToast("Layer created successfully, saving features...", {
        appearance: ToastStatuses.Success,
        autoDismiss: true,
      });
    }

    try {
      await saveFeatures(newLayerId);
      addToast("Features saved successfully!", { appearance: ToastStatuses.Success, autoDismiss: true });
    } catch (error) {
      return addToast("Failed to save features, please try again.", {
        appearance: ToastStatuses.Error,
        autoDismiss: true,
      });
    }

    history.push(`/${site}/admin/mapping`);
  };

  const handleAddFeature = () => {
    if (fieldDefinitions.length === 0) handleAddColumn("Name");
    handleToolSelect(SketchTool.Polygon);
  };

  const sortedFeatures = useMemo(
    () => tableFeatures.toArray().sort((a, b) => a?.attributes.index - b?.attributes.index),
    [tableFeatures],
  );

  return (
    <div className='tw-flex tw-h-full tw-w-full tw-flex-col tw-p-6'>
      <Header as='h1' className='tw-mb-4'>
        <Header.Content>
          <FontAwesomeIcon icon={faLayerGroup} />
          <p className='tw-ml-3 tw-inline-block'>Edit Layer</p>
        </Header.Content>
      </Header>
      <Divider className='tw-mb-8 tw-mt-4' />

      {/* Layer Name Input */}
      <div className='tw-mb-4'>
        <label className='tw-mb-1 tw-block tw-font-semibold'>Name</label>
        <Input
          placeholder='Enter a name for this layer...'
          value={layerName}
          onChange={(e) => {
            setLayerName(e.target.value);
            if (e.target.value.trim() && layerNameError) setLayerNameError(null);
          }}
          fluid
        />
        {layerNameError && <Message error compact className='tw-mb-2 tw-mt-1' content={layerNameError} />}
      </div>

      <div className='mapping-map tw-relative tw-mb-4 tw-h-[55%] tw-max-h-[55%] tw-w-full tw-overflow-x-hidden'>
        {/* Map */}
        <div className='tw-bg-gray-100 tw-h-full tw-w-full tw-overflow-hidden tw-rounded-md' ref={mapRef}></div>

        {/* Toolbar */}
        <MapEditorToolbar
          sketchRef={sketchRef}
          zoomRef={zoomRef}
          handleToolSelect={handleToolSelect}
          selectedTool={selectedTool}
          handleUndo={handleUndo}
          handleRedo={handleRedo}
          canUndo={canUndo}
          canRedo={canRedo}
        />

        {/* Feature Popup */}
        <div
          className={classNames({
            "tw-absolute tw-right-2 tw-top-2 tw-z-20 tw-w-96 tw-transform tw-rounded-md tw-bg-white tw-p-4 tw-shadow-lg tw-transition-all tw-duration-150 tw-ease-out": true,
            "tw-translate-x-0 tw-opacity-100": showFeaturePopup && selectedFeatures.length,
            "tw-translate-x-1/3 tw-opacity-0": !showFeaturePopup || !selectedFeatures.length,
          })}
        >
          <div className='tw-max-h-64 tw-overflow-y-auto'>
            {fieldDefinitions.map((field) => (
              <div key={field.customField.id} className='tw-mb-1 tw-flex tw-items-center'>
                <label className='tw-mr-2 tw-flex-1 tw-text-sm tw-font-bold'>{field.customField.label}</label>
                <Input
                  value={selectedFeatures[0]?.attributes?.[field.customField.id] ?? ""}
                  onChange={(e) =>
                    updateFeatureField(field, selectedFeatures[0], e, selectedFeatures[0].attributes?.graphicId)
                  }
                  size='small'
                  className='tw-flex-[2]'
                >
                  <input className='tw-w-full' />
                </Input>
              </div>
            ))}
          </div>
          {/* Delete feature button */}
          <div className='tw-mt-2 tw-flex tw-justify-end'>
            <Button
              className='tw-mt-2 tw-bg-transparent tw-text-red'
              onClick={(e: any) => deleteFeature(e, tableFeatures, selectedFeatures[0])}
            >
              <Icon name='trash alternate' />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Feature Properties Table */}
      <div
        className={classNames({
          "map-features-table tw-mt-4 tw-flex tw-w-full tw-flex-col tw-overflow-hidden": true,
          "tw-h-[35%]": tableFeatures.length > 0,
        })}
        ref={tableContainerRef}
      >
        <div className='tw-flex tw-items-center tw-justify-between'>
          <h3 className='tw-mb-0'>Features</h3>
          <div className='tw-flex tw-space-x-2'>
            <Button
              className='tw-rounded-md tw-border-[0.5px] tw-border-solid tw-border-black-light tw-border-opacity-30 tw-bg-white tw-px-4 tw-py-2 tw-text-black tw-shadow-sm'
              onClick={handleImportLayer}
            >
              <Icon name='upload' /> Import Layer
            </Button>
            <Button
              className='tw-rounded-md tw-border-[0.5px] tw-border-solid tw-border-black-light tw-border-opacity-30 tw-bg-white tw-px-4 tw-py-2 tw-text-black tw-shadow-sm'
              onClick={handleAddFeature}
            >
              <Icon name='plus' /> Add Feature
            </Button>
          </div>
        </div>

        {tableFeatures.length === 0 && !areFeaturesLoading ? (
          <div className='tw-mt-6 tw-flex tw-h-full tw-items-start tw-justify-start tw-text-left'>
            <div>
              <p className='tw-mb-4'>
                {`Your layer doesn't have any features yet. To add some, start drawing on the map above or click Import Layer or Add Feature.`}
              </p>
            </div>
          </div>
        ) : (
          <Segment
            loading={layerQuery.isLoading}
            className='VZenTable tw-flex-grow tw-overflow-hidden tw-rounded-md tw-border tw-p-0'
            style={{ height: tableHeight }}
          >
            {(isSaving || areFeaturesLoading) && (
              <Dimmer active inverted>
                <Loader />
              </Dimmer>
            )}

            {/* Virtualized Table */}
            <AutoSizer>
              {({ width, height }) => {
                const columnCount = fieldDefinitions.length;
                const minColumnWidth = 140;
                const actionsColumnWidth = 60;
                const totalMinWidth = columnCount * minColumnWidth + actionsColumnWidth;
                const maxWidth = Math.max(width, totalMinWidth);

                return (
                  <div style={{ width, height, overflowY: "hidden", overflowX: "auto" }}>
                    <Table
                      headerHeight={45}
                      rowCount={sortedFeatures.length}
                      rowHeight={45}
                      width={maxWidth}
                      height={height}
                      rowGetter={({ index }) => sortedFeatures[index]}
                      rowClassName={({ index }) => {
                        const graphic = sortedFeatures[index];
                        const graphicId = graphic?.attributes?.graphicId;
                        return classNames({
                          "tw-cursor-pointer": true,
                          "tw-bg-[#DCDFEC]": graphicId === selectedFeatureId,
                        });
                      }}
                      onRowClick={({ rowData }) => {
                        const graphicId = rowData.attributes?.graphicId;
                        if (graphicId) handleSelectFeatureFromTable(graphicId);
                      }}
                      onRowMouseOver={({ rowData }) => {
                        const graphicId = rowData.attributes?.graphicId;
                        if (graphicId) handleFeatureHover(graphicId);
                      }}
                      onRowMouseOut={() => handleFeatureHover(null)}
                    >
                      {/* Dynamic columns based on field definitions */}
                      {fieldDefinitions.map((field, i) => (
                        <Column
                          key={field.customField.id}
                          dataKey={field.customField.id.toString()}
                          width={minColumnWidth}
                          flexGrow={1}
                          headerRenderer={() => (
                            <div className='tw-relative tw-py-1'>
                              <div className='tw-flex tw-items-center tw-justify-between'>
                                {/* Column Label */}
                                <Input
                                  value={field.customField.label}
                                  onChange={(e) => handleUpdateColumnLabel(field.customField.id, e.target.value)}
                                  className='tw-w-full tw-font-bold'
                                  onClick={(e: any) => e.stopPropagation()}
                                >
                                  <input className='tw-w-full tw-font-bold' />
                                </Input>

                                {/* Column Actions Dropdown */}
                                <Dropdown
                                  icon='ellipsis horizontal'
                                  className='tw-ml-4'
                                  direction={
                                    (maxWidth - actionsColumnWidth) / columnCount > 180 || i !== 0 ? "left" : "right"
                                  }
                                  onClick={(e: any) => e.stopPropagation()}
                                >
                                  <Dropdown.Menu>
                                    <Dropdown.Item
                                      icon='bookmark'
                                      text={field.isPrimary ? "Marked as Primary" : "Mark as Primary"}
                                      disabled={field.isPrimary}
                                      onClick={() => handleSetPrimaryColumn(field.customField.id)}
                                    />
                                    <Dropdown.Item
                                      icon='trash alternate'
                                      text='Delete'
                                      className='tw-text-red'
                                      disabled={field.isPrimary}
                                      onClick={() => handleDeleteColumn(field.customField.id)}
                                    />
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </div>
                          )}
                          cellRenderer={({ rowData }) => {
                            const graphic = rowData;
                            const graphicId = graphic.attributes?.graphicId;
                            return (
                              <Input
                                value={graphic.attributes?.[field.customField.id] ?? ""}
                                onChange={(e) => updateFeatureField(field, graphic, e, graphicId)}
                                fluid
                                onClick={(e: any) => e.stopPropagation()}
                              />
                            );
                          }}
                        />
                      ))}

                      {/* Add column button and delete action column */}
                      <Column
                        dataKey='actions'
                        width={actionsColumnWidth}
                        flexGrow={0} // Don't allow column to grow
                        headerRenderer={() => (
                          <div className='tw-flex tw-justify-center'>
                            <Button
                              icon
                              size='mini'
                              onClick={handleAddNewColumn}
                              title='Add Column'
                              className='tw-bg-transparent'
                            >
                              <Icon name='plus' />
                            </Button>
                          </div>
                        )}
                        cellRenderer={({ rowData }) => {
                          const graphic = rowData;
                          return (
                            <div className='tw-flex tw-justify-center'>
                              <Icon
                                name='trash alternate'
                                color='red'
                                link
                                title='Delete Feature'
                                onClick={(e: any) => {
                                  e.stopPropagation();
                                  deleteFeature(e, tableFeatures, graphic);
                                }}
                              />
                            </div>
                          );
                        }}
                      />
                    </Table>
                  </div>
                );
              }}
            </AutoSizer>
          </Segment>
        )}

        <Divider className='tw-mt-1' />

        {/* Deactivate/Reactivate Buttons */}
        <div className='tw-mt-0 tw-flex tw-justify-between'>
          <Button className='tw-bg-transparent tw-text-blue' onClick={() => history.push(`/${site}/admin/mapping`)}>
            Return to List
          </Button>
          <div className='tw-flex tw-space-x-2'>
            {layerId &&
              (layerQuery.data?.deactivatedAt ? (
                <Button className='tw-bg-transparent tw-text-blue' onClick={handleReactivate}>
                  Reactivate
                </Button>
              ) : (
                <Button className='tw-bg-transparent tw-text-red' onClick={() => setIsModalOpen(true)}>
                  Deactivate
                </Button>
              ))}
            <Button
              className='tw-bg-blue tw-px-4 tw-py-0 tw-font-bold tw-text-white'
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </div>

      {/* Deactivation Confirmation Modal */}
      <LayerDeactivateModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        handleDeactivate={handleDeactivate}
      />

      {/* Import Layer Modal */}
      <ImportLayerModal
        isModalOpen={isImportModalOpen}
        setIsModalOpen={setIsImportModalOpen}
        isImporting={isImporting}
        handleFileUpload={handleFileUpload}
        handleImport={handleImport}
        fileContent={fileContent}
        originalKML={originalKML}
        fileType={fileType}
        wipeState={wipeState}
      />
    </div>
  );
}

export default MapManagementEdit;

interface DeactivateModalProps {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDeactivate: () => Promise<void>;
}
export function LayerDeactivateModal({ isModalOpen, setIsModalOpen, handleDeactivate }: DeactivateModalProps) {
  return (
    <Modal size='tiny' open={isModalOpen} onClose={() => setIsModalOpen(false)}>
      <Modal.Header>Confirm Deactivation</Modal.Header>
      <Modal.Content>
        <p>{`Are you sure you want to deactivate this layer?`}</p>
        <p>
          Deactivated layers will no longer be visible on maps and their features will not be included in spatial
          queries.
        </p>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
        <Button negative onClick={handleDeactivate}>
          Deactivate
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
